/* import __COLOCATED_TEMPLATE__ from './phone-office-hours.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import { handoverConfigErrorForConfigType } from 'embercom/lib/operator/fin-voice/number-configuration-helpers';
import type { Schedule } from 'embercom/services/fin-voice-config-service';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: any;
    isOpen: boolean;
    ruleset: $TSFixMe;
    hasContentReadyForFin: boolean;
    phoneNumber?: string;
    numberConfiguration?: FinVoiceNumberConfiguration;
    updateOfficeHours: (phoneNumber: string, schedule: string | undefined) => Promise<void>;
    addAndSaveNewOfficeHoursSchedule: (phoneNumber: string, schedule: any) => Promise<void>;
    updatePercentageRollout: (
      phoneNumber: string,
      rollOutPercentageIn: number | undefined,
      rollOutPercentageNin: number | undefined,
    ) => Promise<void>;
    onPopoverInteraction?: () => void;
  };
}

export default class PhoneOfficeHours extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare router: RouterService;
  @service declare appService: $TSFixMe;

  @tracked officeHoursScheduleComparison: 'in' | 'nin' | 'anytime' = 'anytime';
  @tracked percentageRolloutInHours: number | undefined;
  @tracked percentageRolloutNinHours: number | undefined;
  @tracked selectedOfficeHoursSchedule?: string;

  @tracked newSchedule: Schedule | null = null;
  @tracked showModal = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.populateInitialOfficeHoursSchedules();
  }

  get officeHoursSchedules() {
    return this.finVoiceConfigService.officeHoursSchedules;
  }

  get isSaving() {
    return Boolean(this.numberConfiguration?.isSaving);
  }

  get savingDisabled() {
    return !this.args.phoneNumber || this.isSaving;
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get savedOfficeHoursScheduleComparison() {
    return this.numberConfiguration?.predicates?.officeHoursComparison || 'anytime';
  }

  get savedOfficeHoursSchedule() {
    return this.numberConfiguration?.predicates?.officeHoursSchedule;
  }

  get activeOfficeHoursScheduleDescription() {
    return this.officeHoursSchedules.find(
      (schedule) => schedule.value === this.selectedOfficeHoursSchedule,
    )?.description;
  }

  get activeOfficeHoursScheduleLabel() {
    return this.officeHoursSchedules.find(
      (schedule) => schedule.value === this.selectedOfficeHoursSchedule,
    )?.text;
  }

  get hasSavedPercentageRollout() {
    return Object.keys(this.numberConfiguration?.predicates || {}).some((key) =>
      ['in', 'nin', 'default'].includes(key),
    );
  }

  get contentPageUrl(): string {
    return this.router.urlFor('apps.app.automation.fin-ai-agent.setup', {
      queryParams: {
        activeTab: 'add-content',
      },
    });
  }

  get inHandoverError() {
    return this.getDisabledMessageForNumberConfiguration('in');
  }

  get ninHandoverError() {
    return this.getDisabledMessageForNumberConfiguration('nin');
  }

  get shouldShowPaywall() {
    return this.finVoiceConfigService.shouldShowPaywall;
  }

  @action
  getDisabledMessageForNumberConfiguration(comparison: 'in' | 'nin') {
    let handoverError = handoverConfigErrorForConfigType(
      this.numberConfiguration?.handoverConfiguration,
      comparison,
    );

    if (handoverError) {
      let label = handoverError.label ? this.intl.t(handoverError.label) : undefined;
      return this.intl.t(handoverError.error, { label });
    }

    if (!this.args.hasContentReadyForFin) {
      return this.intl.t(
        'operator.fin.setup.setup-and-go-live.chat-setup-warning.not-content-ready.description',
        {
          contentPageUrl: this.contentPageUrl,
          htmlSafe: 'true',
        },
      );
    }

    if (!this.appService.app.canUseExternalAi) {
      return this.intl.t('operator.fin.setup.setup-and-go-live.set-fin-live.automatic-opt-in', {
        htmlSafe: 'true',
      });
    }

    return undefined;
  }

  @action
  populateInitialOfficeHoursSchedules() {
    this.selectedOfficeHoursSchedule =
      this.savedOfficeHoursSchedule || this.finVoiceConfigService.getDefaultOfficeHoursSchedule();

    let rolloutPercentageIn: number | undefined = 0;
    let rolloutPercentageNin: number | undefined = 0;

    if (this.hasSavedPercentageRollout) {
      rolloutPercentageIn =
        this.numberConfiguration?.predicates?.in?.percentageRollout ||
        this.numberConfiguration?.predicates?.default?.percentageRollout;

      rolloutPercentageNin =
        this.numberConfiguration?.predicates?.nin?.percentageRollout ||
        this.numberConfiguration?.predicates?.default?.percentageRollout;
    } else if (this.numberConfiguration?.predicates?.officeHoursComparison === 'in') {
      rolloutPercentageIn = this.numberConfiguration?.predicates?.percentageRollout;
    } else if (this.numberConfiguration?.predicates?.officeHoursComparison === 'nin') {
      rolloutPercentageNin = this.numberConfiguration?.predicates?.percentageRollout;
    } else {
      rolloutPercentageIn = this.numberConfiguration?.predicates?.percentageRollout;
      rolloutPercentageNin = this.numberConfiguration?.predicates?.percentageRollout;
    }

    this.percentageRolloutInHours = rolloutPercentageIn || 0;
    this.percentageRolloutNinHours = rolloutPercentageNin || 0;

    this.officeHoursScheduleComparison = this.savedOfficeHoursScheduleComparison;
  }

  @action
  openNewScheduleModal() {
    this.newSchedule = this.store.createRecord('office-hours-schedule');
    this.showModal = true;
  }

  @action
  onModalSave(schedule: Schedule) {
    if (!this.args.phoneNumber) {
      return;
    }

    this.selectedOfficeHoursSchedule = schedule.id;
    this.newSchedule = null;

    this.args.addAndSaveNewOfficeHoursSchedule(this.args.phoneNumber, schedule);
  }

  @action
  async updateOfficeHoursSchedule(value: string) {
    if (this.savingDisabled || !this.args.phoneNumber) {
      return;
    }

    this.selectedOfficeHoursSchedule = value;

    await this.args.updateOfficeHours(this.args.phoneNumber, this.selectedOfficeHoursSchedule);
  }

  @action
  async updatePercentageRolloutIn(percentageRollout: number) {
    if (this.savingDisabled || !this.args.phoneNumber) {
      return;
    }

    this.percentageRolloutInHours = percentageRollout;

    await this.args.updatePercentageRollout(
      this.args.phoneNumber,
      this.percentageRolloutInHours,
      this.percentageRolloutNinHours,
    );
  }

  @action
  talkToUs() {
    this.finVoiceConfigService.talkToUs();
  }

  @action
  async updatePercentageRolloutNin(percentageRollout: number) {
    if (this.savingDisabled || !this.args.phoneNumber) {
      return;
    }

    this.percentageRolloutNinHours = percentageRollout;

    await this.args.updatePercentageRollout(
      this.args.phoneNumber,
      this.percentageRolloutInHours,
      this.percentageRolloutNinHours,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::NumberConfiguration::PhoneOfficeHours': typeof PhoneOfficeHours;
  }
}
